/**
 * Money helpers. ByQuick stores all currency as INTEGER minor units (paise).
 * Never use floats for money. Never call `toLocaleString` / `Intl.NumberFormat`
 * for currency outside this module — this is the single rendering choke-point.
 *
 * See AGENTS.md §"i18n, money, dates" and .cursor/rules/i18n-money-date.mdc.
 */

const INR_FORMATTER = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const INR_COMPACT_FORMATTER = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

export type Paise = number;

export function assertPaise(value: number, context = 'amount'): asserts value is Paise {
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    throw new TypeError(
      `Money must be an integer number of paise; received ${value} for "${context}". ` +
        `If you have rupees, multiply by 100 at the boundary (never at render).`,
    );
  }
}

/**
 * Render an amount in paise as a ₹-prefixed INR string ("₹1,23,450.00").
 * Indian-number grouping (lakh/crore) is applied by `en-IN`.
 */
export function formatMoney(paise: Paise): string {
  assertPaise(paise);
  return INR_FORMATTER.format(paise / 100);
}

/** Like formatMoney, but no decimals — for headline KPI tiles. */
export function formatMoneyCompact(paise: Paise): string {
  assertPaise(paise);
  return INR_COMPACT_FORMATTER.format(paise / 100);
}

/** Convert a rupee amount (from an input field) to paise. Rejects non-finite values. */
export function rupeesToPaise(rupees: number): Paise {
  if (!Number.isFinite(rupees)) {
    throw new TypeError(`rupeesToPaise: received non-finite value ${rupees}`);
  }
  return Math.round(rupees * 100);
}

/** Convert paise to rupees for display / editing only — never for arithmetic. */
export function paiseToRupees(paise: Paise): number {
  assertPaise(paise);
  return paise / 100;
}
